import React from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Code,
  Collapse,
  useDisclosure
} from '@chakra-ui/react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('Error Boundary caught an error:', error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorId={this.state.errorId}
          onRetry={this.handleRetry}
          fallback={this.props.fallback}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error, errorInfo, errorId, onRetry, fallback }) => {
  const { isOpen, onToggle } = useDisclosure();

  // If a custom fallback is provided, use it
  if (fallback) {
    return fallback({ error, errorInfo, onRetry });
  }

  return (
    <Box maxW="2xl" mx="auto" p={6} mt={10}>
      <Alert status="error" flexDirection="column" alignItems="center" textAlign="center" borderRadius="lg">
        <AlertIcon boxSize="40px" mr={0} />
        <AlertTitle mt={4} mb={1} fontSize="lg">
          Something went wrong!
        </AlertTitle>
        <AlertDescription maxWidth="sm" mb={4}>
          We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.
        </AlertDescription>
      </Alert>

      <VStack spacing={4} mt={6}>
        <Button colorScheme="blue" onClick={onRetry}>
          Try Again
        </Button>

        <Button variant="ghost" size="sm" onClick={onToggle}>
          {isOpen ? 'Hide' : 'Show'} Error Details
        </Button>

        <Collapse in={isOpen} animateOpacity>
          <Box p={4} bg="dark.700" borderRadius="md" maxW="full" overflow="auto">
            <VStack align="start" spacing={3}>
              <Text fontSize="sm" fontWeight="bold">Error ID: {errorId}</Text>

              {error && (
                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>Error Message:</Text>
                  <Code p={2} bg="red.900" color="red.100" borderRadius="md" display="block" whiteSpace="pre-wrap">
                    {error.toString()}
                  </Code>
                </Box>
              )}

              {errorInfo && errorInfo.componentStack && (
                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>Component Stack:</Text>
                  <Code p={2} bg="dark.600" color="gray.300" borderRadius="md" display="block" whiteSpace="pre-wrap" fontSize="xs">
                    {errorInfo.componentStack}
                  </Code>
                </Box>
              )}
            </VStack>
          </Box>
        </Collapse>
      </VStack>
    </Box>
  );
};

// Higher-order component for easier usage
export const withErrorBoundary = (Component, fallback) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

export default ErrorBoundary;
